import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import decorators from 'utils/decorators';
import checkoutApi from 'services/api/checkout';
import OrderUtils from 'utils/Order';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import Actions from 'actions/Actions';
import store from 'store/Store';
import resourceWrapper from 'utils/framework/resourceWrapper';
import Flush from 'utils/localStorage/Flush';
import locationUtils from 'utils/Location';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import {
    ALTERNATE_PAYMENT_LIST,
    ALTERNATE_PAYMENT_TYPES,
    BOPIS_PICKUP_ERRORS,
    FC_CHECKOUT_PATH,
    FC_CHECKOUT_SECTIONS,
    PAYMENT_ERRORS,
    SDD_ERRORS,
    SECTION_NAMES,
    VENMO_PAYMENT_GROUP
} from 'constants/frictionlessCheckout';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import {
    Divider, Flex, Link, Text
} from 'components/ui';
import ChatEntry from 'components/Content/CustomerService/ChatWithUs/ChatEntry';
import { CHAT_ENTRY } from 'constants/chat';
import ErrorConstants from 'utils/ErrorConstants';
import cookieUtils from 'utils/Cookies';
import agentAwareUtils from 'utils/AgentAware';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import Storage from 'utils/localStorage/Storage';
import CheckoutUtils from 'utils/Checkout';
import creditCardUtils from 'utils/CreditCard';
import FrictionlessCheckoutEN from 'utils/locales/FrictionlessCheckout/locales/FrictionlessCheckout_en_US';
import FrictionlessCheckoutFR from 'utils/locales/FrictionlessCheckout/locales/FrictionlessCheckout_fr_CA';
import PaymentEN from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_en_US';
import PaymentFR from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_fr_CA';

const { ERROR_KEYS, ERROR_CODES } = ErrorConstants;

const getText = localeUtils.getLocalisedDataFromResource(FrictionlessCheckoutEN, FrictionlessCheckoutFR);
const getPaymentText = localeUtils.getLocalisedDataFromResource(PaymentEN, PaymentFR);

const maxAmountToCheckout = Sephora.configurationSettings.afterpayConfigurations?.maxAmountToCheckout || 2000;
const PAYMENT_GROUPS = { CREDIT_CARD: 'CreditCardPaymentGroup' };
const HIGH_REDEEM_POINTS_THRESHOLD = 750;

function isHigherRedeemPointsOrder(orderDetails = store.getState().order.orderDetails) {
    const redeemedBiPoints = orderDetails?.items?.redeemedBiPoints || 0;
    const isBopisOrder = orderDetails?.header?.isBopisOrder;

    return redeemedBiPoints >= HIGH_REDEEM_POINTS_THRESHOLD && OrderUtils.isZeroCheckout() && !isBopisOrder;
}

const paymentServices = {
    payWithKlarna: {
        paymentService: 'Klarna',
        paymentName: 'payWithKlarna',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.KLARNA,
        limit: 1000
    },
    payWithAfterpay: {
        paymentService: localeUtils.isUS() ? 'Cash App Afterpay' : 'Afterpay',
        paymentName: 'payWithAfterpay',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.AFTERPAY,
        limit: maxAmountToCheckout
    },
    payWithPaze: {
        paymentService: 'Paze',
        paymentName: 'payWithPaze',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.PAZE,
        limit: maxAmountToCheckout
    },
    payWithVenmo: {
        paymentService: 'Venmo',
        paymentName: 'payWithVenmo',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.VENMO,
        limit: maxAmountToCheckout
    }
};

function setShippingMethod({
    orderId, newShippingMethodId, shippingGroupId, successCallback, failureCallback, message, updateWaiveShipping
}) {
    let shipOptionsSendData = {
        orderId,
        shippingGroupId,
        shippingMethodId: newShippingMethodId
    };

    if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
        //If it is Sephora Mirror and extension is installed, waive shipping and handling checkbox is displayed
        shipOptionsSendData = {
            ...shipOptionsSendData,
            waiveShippingFee: message || false
        };
        updateWaiveShipping(message || false);
    }

    return decorators
        .withInterstice(
            checkoutApi.setShippingMethod,
            INTERSTICE_DELAY_MS
        )(shipOptionsSendData)
        .then(() => {
            successCallback();
        })
        .catch(errorData => failureCallback(errorData));
}

function checkPromoEligibility(checkedShippingMethod, orderDetails) {
    const { items = {} } = orderDetails;
    const { promoShippingMethods = [], appliedPromotions = [] } = items;

    const ineligibleShippingPromotions = appliedPromotions
        .filter(
            appliedPromo =>
                appliedPromo.promoShippingMethods &&
                appliedPromo.promoShippingMethods.every(promoMethod => promoMethod !== checkedShippingMethod.shippingMethodType)
        )
        .map(ineligiblePromo => ineligiblePromo.couponCode);

    if (promoShippingMethods.length && ineligibleShippingPromotions.length) {
        return false;
    }

    return true;
}

function getPaymentSectionProps(paymentName, checkoutEnabled) {
    const sectionProps = paymentServices[paymentName] ? { ...paymentServices[paymentName] } : {};
    sectionProps.paymentCheckoutEnabled = checkoutEnabled;
    sectionProps.limit = localeUtils.isFRCanada()
        ? `${sectionProps.limit}${localeUtils.CURRENCY_SYMBOLS.CA_FR}`
        : `${localeUtils.CURRENCY_SYMBOLS.US}${sectionProps.limit}`;

    return sectionProps;
}

function validateCreditCardErrors(payment) {
    if (!payment.isMigrated) {
        return getText('creditCardMigratedError');
    }

    return null;
}

function validatePaymentGroup(paymentGroups, messages, isOrderComplete) {
    const handlerMapper = {
        [PAYMENT_GROUPS.CREDIT_CARD]: validateCreditCardErrors
    };

    const errorMessages = [];

    paymentGroups.forEach(currentPaymentGroup => {
        if (handlerMapper[currentPaymentGroup?.paymentGroupType]) {
            const values = handlerMapper[currentPaymentGroup?.paymentGroupType](currentPaymentGroup?.paymentGroup);

            if (values) {
                errorMessages.push(values);
            }
        }
    });

    if (messages?.type === 'error') {
        errorMessages.push(...messages?.messages);
    }

    if (!messages && !isOrderComplete) {
        errorMessages.push(getText('cardMissingDetailsDefault'));
    }

    return errorMessages.length > 0 ? errorMessages : getText('defaultMessage');
}

function setErrorsFromCheckoutAPI(errorData) {
    const sectionErrors = {};

    if (errorData?.errors?.invalidAddress && errorData?.errorCode === -10180 && SDD_ERRORS.includes(errorData?.key)) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.errorMessages?.join(' ');
    }

    if (errorData?.scheduledAddrChangeMessage) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.scheduledAddrChangeMessage?.[0]?.messages?.join(' ');
    }

    if (errorData?.errors?.sameDaySkuOOSException) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.errorMessages?.join(' ');
    }

    return sectionErrors;
}

function setDeliverToErrors(message, isGiftCardSection = false) {
    const sectionErrors = {};
    sectionErrors[isGiftCardSection ? SECTION_NAMES.GIFT_CARD_DELIVERY : SECTION_NAMES.DELIVER_TO] = message;

    return sectionErrors;
}

function checkDeliverToAndGCErrors(orderDetails, isSDUOnlyOrder, isBopisOrder, sectionToBeConfirmed) {
    const shippingGroups = orderDetails?.shippingGroups?.shippingGroupsEntries || [];

    const sectionErrors = {};

    // Skip gift card delivery error when a gift card address mismatch will be handled by
    // GiftAddressWarningModal via proceedAfterValidation. Without this guard, the shipping
    // group's isComplete=false adds a blocking error that prevents the modal from showing.
    const isGiftCardAddressMismatch = sectionToBeConfirmed === SECTION_NAMES.GIFT_CARD_DELIVERY && orderDetails?.header?.isAddressMisMatch;

    shippingGroups.forEach(shippingGroup => {
        if (!shippingGroup?.shippingGroup?.isComplete && !isBopisOrder && !isSDUOnlyOrder) {
            const sectionName =
                shippingGroup?.shippingGroupType === OrderUtils.SHIPPING_GROUPS.GIFT ? SECTION_NAMES.GIFT_CARD_DELIVERY : SECTION_NAMES.DELIVER_TO;

            if (sectionName === SECTION_NAMES.GIFT_CARD_DELIVERY && isGiftCardAddressMismatch) {
                return;
            }

            sectionErrors[sectionName] = getText('defaultMessage');
        }
    });

    return sectionErrors;
}

function isPickupInfoComplete(orderDetails) {
    const details = orderDetails || store.getState().order.orderDetails || {};
    const { pickup = {}, header = {} } = details;

    // Only validate pickup info for guest BOPIS orders
    if (header.isBopisOrder && header.isGuestOrder) {
        return pickup.isComplete === true;
    }

    return true;
}

function isFCFinalReleaseEnabled() {
    const isFCFinalReleaseCookieEnabled = cookieUtils.isFCFinalReleaseEnabled();
    const isFCFinalReleaseKSEnabled = Sephora.configurationSettings.frictionlessCheckout?.finalRelease?.isEnabled;

    return isFCFinalReleaseCookieEnabled && isFCFinalReleaseKSEnabled;
}

function isAnyAlternatePaymentSelectedInState() {
    const state = store.getState();

    return Boolean(
        state?.afterpay?.isSelected || state?.klarna?.isSelected || state?.venmo?.isSelected || state?.paze?.isSelected || state?.paypal?.isSelected
    );
}

// eslint-disable-next-line complexity
function checkForCheckoutSectionsCompleteness(orderDetails, isCvvRequired = false, sectionToBeConfirmed = null, hasPendingCreditCard = false) {
    let sectionErrors = {};

    const hasSDD = orderDetails?.header?.isSameDayOrder;
    const isSDUOnlyOrder = OrderUtils.isSDUOnlyOrder(orderDetails);
    const isPaymentNotComplete = orderDetails?.paymentGroups?.paymentGroupsEntries?.some(
        paymentGroup => paymentGroup?.paymentGroup.isComplete === false
    );
    const isBopisOrder = orderDetails?.header?.isBopisOrder;

    // Ensure if-conditions follow the checkout layout order.
    // Start with "Deliver To" if it's the first section, and so on.
    // This keeps SectionErrors keys aligned with layout sequence,
    // enabling smooth scroll to the first error.

    // addressMismatch excluded: proceedAfterValidation handles it via GiftAddressWarningModal
    // isGiftCardAddressMismatch needed to skip gift_card_delivery confirmation error when
    // giftCardAddressNeedsToBeConfirmed is true AND a mismatch exists
    const isGiftCardAddressMismatch = sectionToBeConfirmed === SECTION_NAMES.GIFT_CARD_DELIVERY && orderDetails?.header?.isAddressMisMatch;

    const deliverToAndGCErrors = checkDeliverToAndGCErrors(orderDetails, isSDUOnlyOrder, isBopisOrder, sectionToBeConfirmed);

    if (deliverToAndGCErrors) {
        sectionErrors = {
            ...sectionErrors,
            ...deliverToAndGCErrors
        };
    }

    // Fallback: Validate pickup info is complete and saved in case placeOrderPreHook is not set
    if (isBopisOrder && !isPickupInfoComplete(orderDetails)) {
        sectionErrors[SECTION_NAMES.BOPIS_PICKUP_INFO] = getText('defaultMessage');
    }

    if (hasSDD && !isSDUOnlyOrder && !orderDetails?.shippingGroups?.shippingGroupsEntries?.[0]?.shippingGroup?.shippingMethod?.isComplete) {
        // SDD Incomplete error
        sectionErrors[SECTION_NAMES.SDD] = getText('defaultMessage');
    }

    if (!orderDetails?.header?.shippingGroups?.[0]?.isComplete && !isBopisOrder && !isSDUOnlyOrder) {
        // Shipping method incomplete error
        sectionErrors[SECTION_NAMES.SHIPPING_METHOD] = getText('defaultMessage');
    }

    const paymentMessages = orderDetails?.paymentGroups?.paymentMessages ?? Empty.Array;
    const hasPaymentMessages = paymentMessages.length > 0;

    // For $0 checkout with CVV validation, skip payment error because CVV modal
    // will be shown later in the flow (in proceedAfterValidation)
    const isZeroDollarOrderWithCVVValidation = OrderUtils.isZeroDollarOrderWithCVVValidation();

    if (!hasPendingCreditCard && !isZeroDollarOrderWithCVVValidation && (isPaymentNotComplete || hasPaymentMessages)) {
        // payment method incomplete error
        sectionErrors[SECTION_NAMES.PAYMENT] = validatePaymentGroup(
            orderDetails?.paymentGroups?.paymentGroupsEntries,
            orderDetails?.paymentGroups?.paymentMessages?.[0],
            orderDetails?.header?.isComplete
        );
    }

    if (isCvvRequired) {
        sectionErrors[SECTION_NAMES.PAYMENT] = getText('confirmCVV');
    }

    if (!orderDetails?.items?.isBIPointsAvailable) {
        // BI Points unavailable error.
        sectionErrors[SECTION_NAMES.BI_BENEFITS] = getText('BIUnavailable');
    }

    const shouldSkipPaymentConfirmSelectionError =
        sectionToBeConfirmed === SECTION_NAMES.PAYMENT && isBopisOrder && isAnyAlternatePaymentSelectedInState();

    if (sectionToBeConfirmed && !shouldSkipPaymentConfirmSelectionError && !isGiftCardAddressMismatch) {
        const hasNoPaymentGroups = !(orderDetails?.paymentGroups?.paymentGroupsEntries?.length > 0);
        const isRewardsOnlyZeroDollarOrder =
            isFCFinalReleaseEnabled() && isHigherRedeemPointsOrder(orderDetails) && !isZeroDollarOrderWithCVVValidation && !isBopisOrder;
        const shouldShowAddPaymentMethodMessage =
            sectionToBeConfirmed === SECTION_NAMES.PAYMENT && isRewardsOnlyZeroDollarOrder && hasNoPaymentGroups && !hasPendingCreditCard;

        sectionErrors[sectionToBeConfirmed] = shouldShowAddPaymentMethodMessage ? getText('addPaymentMethod') : getText('confirmAddress');
    }

    return sectionErrors;
}

const ORDER_FRAUD_ERROR_MAP = {
    'checkout.placeOrder.fraud.error.forter_no_fix': {
        errorCode: -8,
        modalTitle: 'error'
    },
    'checkout.placeOrder.fraud.error.client_resolve_coupon': {
        errorCode: -8,
        modalTitle: 'adjustBasket'
    },
    'checkout.placeOrder.fraud.error.client_resolve_sale': {
        errorCode: -8,
        modalTitle: 'adjustBasket'
    },
    'checkout.placeOrder.fraud.error.contact_cs': {
        errorCode: -8,
        modalTitle: 'verificationRequired',
        modalMessage: () => {
            const getFormattedText = resourceWrapper(getText);

            return (
                <Flex
                    flexDirection='column'
                    gap={4}
                >
                    <Text
                        is='p'
                        children={getFormattedText(
                            'contactCS',
                            false,
                            <Link
                                color='blue'
                                underline
                                href='tel:18777374672'
                                children={getText('csPhone')}
                            />
                        )}
                    />
                    <Text is='p'>
                        <Text children={getText('csHours')} />
                        <br />
                        <Text
                            children={getFormattedText(
                                'monFriHours',
                                false,
                                <Text
                                    fontWeight='bold'
                                    children={getText('monToFri')}
                                />
                            )}
                        />
                        <br />
                        <Text
                            children={getFormattedText(
                                'satSunHours',
                                false,
                                <Text
                                    fontWeight='bold'
                                    children={getText('satToSun')}
                                />
                            )}
                        />
                    </Text>
                    <Divider />
                    <ChatEntry type={CHAT_ENTRY.fraudErrCheckout} />
                    <Divider />
                </Flex>
            );
        }
    }
};

function isGuestOrder() {
    return store.getState().order.orderDetails.header.isGuestOrder;
}

const redirectToBasket = () => {
    //remove basket cache so that OOS items show up on basket page with warning message
    Flush.flushBasket();
    const path = '/basket';

    locationUtils.setLocation(path);
};

const redirectOnSpecificErrors = error => {
    if (
        error.key === ERROR_CODES.NOT_ENOUGH_BI_POINTS ||
        error.key === ERROR_CODES.NOT_ENOUGH_POINTS_ERROR ||
        // errorKey for this errors is unknown
        error.errorCode === ERROR_KEYS.SAME_DAY_DISABLED ||
        error.errorCode === ERROR_KEYS.SDU_MISSING_PROFILE_DETAILS
    ) {
        redirectToBasket();
    }
};

function getOrderFraudError(errorData) {
    const defaultErrorMessage = getText('defaultErrorMessage');
    const { errorCode, errorMessages, key: errorKey } = errorData || Empty.Object;

    let isOrderDeclined = false;
    let orderDeclineAction = Empty.Function;

    if (errorKey && Object.hasOwn(ORDER_FRAUD_ERROR_MAP, errorKey) && errorCode === -8) {
        const { modalTitle, modalMessage } = ORDER_FRAUD_ERROR_MAP[errorKey];

        isOrderDeclined = true;

        orderDeclineAction = () => {
            const isGuestCheckout = isGuestOrder();

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText(isGuestCheckout ? 'error' : modalTitle),
                    message: isGuestCheckout
                        ? getText('guestErrorMessage')
                        : modalMessage
                            ? modalMessage()
                            : errorMessages?.length > 0
                                ? errorMessages?.join(' ')
                                : defaultErrorMessage,
                    buttonText: getText('ok'),
                    callback: redirectToBasket,
                    cancelCallback: redirectToBasket
                })
            );
        };
    }

    return {
        isOrderDeclined,
        orderDeclineAction
    };
}

function isSectionLevelError(key) {
    return [...SDD_ERRORS, ...PAYMENT_ERRORS, ...BOPIS_PICKUP_ERRORS].includes(key);
}

function openInfoModal(errorData) {
    const errorMessage = errorData?.errorMessages?.join(' ');
    const htmlMessage = errorMessage.replace(/\n/g, '<br />');
    const forceHtmlMessage = errorMessage !== htmlMessage;
    store.dispatch(
        Actions.showInfoModal({
            isOpen: true,
            isHtml: forceHtmlMessage,
            title: getText('error'),
            message: forceHtmlMessage ? htmlMessage : errorMessage,
            buttonText: getText('ok'),
            cancelCallback: () => redirectOnSpecificErrors(errorData),
            callback: () => redirectOnSpecificErrors(errorData)
        })
    );
}

function isPaymentVerificationFailure(errorData) {
    // Only apply this logic in Release 4
    if (!isFCFinalReleaseEnabled()) {
        return false;
    }

    // Check for the specific CHECKOUT_GENERIC_ERROR key in the errors object
    const errors = errorData?.errors;

    if (!errors) {
        return false;
    }

    // Check if the specific checkout.cc.auth.generic.error key exists
    return ErrorConstants.ERROR_CODES.CHECKOUT_GENERIC_ERROR in errors;
}

function getErrorStatesFromSubmitOrderAPI(errorData) {
    const { key, errorMessages } = errorData;
    const { isOrderDeclined, orderDeclineAction } = getOrderFraudError(errorData);
    const defaultErrorMessage = getText('defaultErrorMessage');

    if (errorData) {
        FrictionlessCheckoutBindings.trackPlaceOrderErrorAnalytics(errorData);
    }

    if (isOrderDeclined) {
        orderDeclineAction();

        return null;
    } else if (isPaymentVerificationFailure(errorData)) {
        openInfoModal(errorData);

        return {
            [SECTION_NAMES.PAYMENT]: getPaymentText('paymentVerificationError')
        };
    } else if (isSectionLevelError(key)) {
        let sectionName = '';
        const sectionErrors = {};

        // Ensure if-conditions follow the checkout layout order.
        // Start with "Deliver To" if it's the first section, and so on.
        // This keeps SectionErrors keys aligned with layout sequence,
        // enabling smooth scroll to the first error.

        if (BOPIS_PICKUP_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.BOPIS_PICKUP_INFO;
        }

        if (SDD_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.SDD;
        }

        if (PAYMENT_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.PAYMENT;
        }

        sectionErrors[sectionName] = errorMessages?.length > 0 ? errorMessages?.join(' ') : defaultErrorMessage;

        return sectionErrors;
    } else {
        if (!errorData?.errorMessages?.length) {
            //adds default error message when there are no messages from API call.
            errorData.errorMessages = [defaultErrorMessage];
        }

        openInfoModal(errorData);

        return null;
    }
}

function getPaymentMethodName() {
    const orderDetails = store.getState().order.orderDetails;
    const paymentGroupEntry = orderDetails?.paymentGroups?.paymentGroupsEntries?.[0];

    if (paymentGroupEntry?.paymentGroupType === PAYMENT_GROUPS.CREDIT_CARD) {
        return 'standard';
    } else {
        return `continue with ${paymentGroupEntry?.paymentGroup?.paymentDisplayInfo}`;
    }
}

function getPromosData() {
    const promotions = store.getState()?.order?.orderDetails?.promotion || Empty.Object;
    const promoCodes = [],
        promoDisplayName = [],
        promotionIds = [],
        promotionTypes = [],
        sephoraPromotionTypes = [];

    if (Array.isArray(promotions.appliedPromotions)) {
        promotions.appliedPromotions.forEach(
            ({
                couponCode = null, displayName = null, promotionId = null, promotionType = null, sephoraPromotionType = null
            }) => {
                promoCodes.push(couponCode);
                promoDisplayName.push(displayName);
                promotionIds.push(promotionId);
                promotionTypes.push(promotionType);
                sephoraPromotionTypes.push(sephoraPromotionType);
            }
        );
    }

    return {
        promoCodes,
        promoDisplayName,
        promotionIds,
        promotionTypes,
        sephoraPromotionTypes
    };
}

function sectionThatNeedsToBeConfirmed(order) {
    if (order.giftCardAddressNeedsToBeConfirmed) {
        return SECTION_NAMES.GIFT_CARD_DELIVERY;
    }

    if (order.addressNeedsToBeConfirmed) {
        return SECTION_NAMES.DELIVER_TO;
    }

    if (order.shippingMethodNeedsToBeConfirmed) {
        return SECTION_NAMES.SHIPPING_METHOD;
    }

    if (order.paymentNeedsToBeConfirmed) {
        return SECTION_NAMES.PAYMENT;
    }

    return null;
}

function isGiftCardAddressComplete(orderDetails) {
    const shipGroup = OrderUtils.getPhysicalGiftCardShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

function isCVVMissing(headerPaymentGroups = []) {
    return headerPaymentGroups.some(group => 'securityCodeMissingMsg' in group);
}

/**
 * Detects whether a BOPIS order requires CVV entry.
 *
 * BOPIS orders do not include `securityCodeMissingMsg` on `header.paymentGroups` the way GIS orders
 * do, so the standard `isCVVMissing` check cannot be used. Instead, the presence of a credit card
 * paymentGroup with `isComplete: false` indicates CVV is required (vs. no CC on file at all).
 *
 * @param {Object} orderDetails - The full order details object.
 * @param {Object} [orderDetails.header] - Order header containing the BOPIS flag.
 * @param {boolean} [orderDetails.header.isBopisOrder] - True when this is a BOPIS order.
 * @returns {boolean} True when the order is BOPIS AND a credit card paymentGroup exists with
 *                    `isComplete === false`. False otherwise (non-BOPIS, no CC, or CC complete).
 */
function isCVVMissingForBopis(orderDetails) {
    if (!orderDetails?.header?.isBopisOrder) {
        return false;
    }

    const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails);

    return Boolean(creditCardPaymentGroup) && creditCardPaymentGroup.isComplete === false;
}

function hasExpiredCardInOrder(paymentOptions) {
    return (paymentOptions?.creditCards || Empty.Array).some(card => card.isCardInOrder && card.isExpired);
}

function isPaymentInOrderComplete(orderDetails) {
    const orderState = store.getState().order;
    const isZeroDollarOrderWithCVVValidation = OrderUtils.isZeroDollarOrderWithCVVValidation();
    const headerPaymentGroups = orderDetails.header.paymentGroups || [];
    const isSDUOnlyOrder = OrderUtils.isSDUOnlyOrder(orderDetails);
    const hasAutoReplenishItems = OrderUtils.hasAutoReplenishItems(orderDetails);
    const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };

    // When the CC in the order is expired, don't short-circuit to "complete" — the user must be
    // routed to Payment L2 so the expired card renders disabled and they can pick a different one.
    if (
        isFCFinalReleaseEnabled() &&
        !hasExpiredCardInOrder(orderState.paymentOptions) &&
        (orderState?.pendingCreditCard || isCVVMissing(headerPaymentGroups) || isZeroDollarOrderWithCVVValidation)
    ) {
        return true; // dont return an error if we have a pending CC to process or if the card has CVV missing.
    }

    if ((isSDUOnlyOrder || hasAutoReplenishItems || isZeroDollarOrderWithCVVValidation) && !creditCardPaymentGroup.isComplete) {
        return false;
    }

    // $0 order with high BI points redeemed requires a saved CC even if headerPaymentGroups is empty
    const isBopisOrder = orderDetails.header.isBopisOrder;
    const zeroDollarHigherRedeemPointsNotCVV = OrderUtils.isZeroCheckout() && !isBopisOrder && !isZeroDollarOrderWithCVVValidation;

    if (zeroDollarHigherRedeemPointsNotCVV && !orderState.paymentOptions?.creditCards?.length) {
        return false;
    }

    // PayPal is handled differently: the payment group is only marked as complete after the user finishes the order,
    // but we want to keep track of PayPal as the selected payment method before placing the order. Check if it's PayPal
    // and treat it as completed even if not yet marked as complete.
    const payPalPaymentGroup = OrderUtils.getPayPalPaymentGroup(orderDetails);
    const isPayPalSelected = Boolean(payPalPaymentGroup) && orderDetails?.priceInfo?.paypalAmount !== undefined;

    if (!isPayPalSelected) {
        for (let i = 0; i < headerPaymentGroups.length; i++) {
            const group = headerPaymentGroups[i];

            if (!group.isComplete) {
                return false;
            }
        }
    }

    return true;
}

function isPaymentSectionComplete(orderDetails, paymentOptions, shouldCheckForAlternatePayExpanded) {
    const isBopisOrder = orderDetails?.header?.isBopisOrder;

    if (isFCFinalReleaseEnabled() && isBopisOrder && isAnyAlternatePaymentSelectedInState()) {
        return true;
    }

    // if Klarna, Afterpay, Paze are a selected payment method -> always mark
    // Payment Section as incomplete.

    //Have expanded payment section for Venmo and paze only if its not FC final release.
    const paymentTypes = ['klarna', 'afterpay', ...(!isFCFinalReleaseEnabled() ? ['venmo', 'paze'] : Empty.Array)];

    const selectedButNotDefault = paymentTypes.some(type => {
        const group = OrderUtils.getPaymentGroup(orderDetails, OrderUtils.PAYMENT_GROUP_TYPE[type.toUpperCase()]);
        const isDefault = paymentOptions.defaultPayment === type;

        return group && !isDefault;
    });

    // keeps the payment section expanded
    if (shouldCheckForAlternatePayExpanded && selectedButNotDefault) {
        return false;
    }

    return isPaymentInOrderComplete(orderDetails);
}

function isShipAddressComplete(orderDetails) {
    const shipGroup = OrderUtils.getHardGoodShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

const normalizeZip = zip => (zip || '').replace(/[^0-9A-Z]/gi, '');

const filterAddressByPreferredZipCode = (preferredZipCode, addressList) => {
    if (!addressList) {
        return null;
    }

    const sliceNumber = localeUtils.isCanada() ? 3 : 5;
    const normPref = normalizeZip(preferredZipCode).slice(0, sliceNumber);

    return addressList.filter(address => {
        const normAddr = normalizeZip(address.postalCode).slice(0, sliceNumber);

        return normPref === normAddr;
    }).length;
};

function isSameDayShipAddressComplete(orderDetails) {
    const shipGroup = OrderUtils.getSameDayShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

function isPaymentComplete(checkForNewUser = false, shouldCheckForAlternatePayExpanded = true) {
    const { orderDetails, paymentOptions } = store.getState().order;
    const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
    const isSephoraCardType = OrderUtils.isSephoraCardType(creditCardPaymentGroup);
    const isSephoraCardEdit = Storage.session.getItem(LOCAL_STORAGE.EDIT_SEPHORA_CARD);
    const shouldEditSephoraCard = isSephoraCardEdit && !isSephoraCardType;

    const paymentGroupsEntries = orderDetails.paymentGroups && orderDetails.paymentGroups.paymentGroupsEntries;

    if (
        checkForNewUser &&
        paymentGroupsEntries.length === 1 &&
        paymentGroupsEntries[0].paymentGroupType === 'CreditCardPaymentGroup' &&
        paymentGroupsEntries[0].paymentGroup.cardNumber.length === 0
    ) {
        //returning true as we dont want to show error when user doesn even have any payment method saved.
        return true;
    }

    if (shouldEditSephoraCard || !isPaymentSectionComplete(orderDetails, paymentOptions, shouldCheckForAlternatePayExpanded)) {
        return false;
    } else {
        return true;
    }
}

function isDeliverToComplete(addressList = []) {
    const { orderDetails } = store.getState().order;
    const preferredZipCode = store.getState().user.preferredZipCode;
    const hardGoodShipGroup = OrderUtils.getHardGoodShippingGroup(orderDetails);
    const sameDayShipGroup = OrderUtils.getSameDayShippingGroup(orderDetails);
    const sduShipGroup = OrderUtils.getSDUShippingGroup(orderDetails);
    const hasSDUOnlyInSddBasket = OrderUtils.hasSDUOnlyInSddBasket(orderDetails);
    const numberOfAddressesByPreferredZipCode = hasSDUOnlyInSddBasket
        ? addressList.length
        : filterAddressByPreferredZipCode(preferredZipCode, addressList);
    const isDefaultAddressSelectedForSDD = sameDayShipGroup?.address?.isDefault;

    if (hardGoodShipGroup && !isShipAddressComplete(orderDetails)) {
        return false;
    } else if (
        sameDayShipGroup &&
        !hasSDUOnlyInSddBasket &&
        (!isSameDayShipAddressComplete(orderDetails) ||
            (addressList?.length &&
                (numberOfAddressesByPreferredZipCode === 0 || (numberOfAddressesByPreferredZipCode > 1 && !isDefaultAddressSelectedForSDD))))
    ) {
        return false;
    } else if (sduShipGroup && !hasSDUOnlyInSddBasket && addressList?.length && numberOfAddressesByPreferredZipCode === 0) {
        return false;
    } else {
        return true;
    }
}

function isGiftCardDeliveryComplete() {
    const { orderDetails } = store.getState().order;
    const physicalGiftCard = OrderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
    const sameDayShipGroup = OrderUtils.getSameDayShippingGroup(orderDetails);

    //we dont render Gift Card section if we have SDD items in basket
    if (physicalGiftCard && !isGiftCardAddressComplete(orderDetails) && !sameDayShipGroup) {
        return false;
    } else {
        return true;
    }
}

// Map of section names to their completion checkers
const SECTION_COMPLETION_CHECKERS = {
    payment: () => isPaymentComplete(true, false),
    deliverTo: () => isDeliverToComplete(),
    pickupInfo: () => isPickupInfoComplete(),
    giftCardDeliverTo: () => isGiftCardDeliveryComplete()
};

function updateCheckoutURLBasedOnOrderCompleteness(addressList) {
    let newPath = FC_CHECKOUT_PATH;
    const currentPath = store.getState().historyLocation.path;

    if (!isGiftCardDeliveryComplete()) {
        newPath += `/${FC_CHECKOUT_SECTIONS.GIFT_CARD_DELIVERY.path}`;
    } else if (!isPickupInfoComplete()) {
        newPath += `/${FC_CHECKOUT_SECTIONS.PICKUP_INFO.path}`;
    } else if (!isDeliverToComplete(addressList)) {
        newPath += `/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}`;
    } else if (!isPaymentComplete()) {
        newPath += `/${FC_CHECKOUT_SECTIONS.PAYMENT.path}`;
    }

    if (currentPath === FC_CHECKOUT_PATH) {
        store.dispatch(historyLocationActions.replaceLocation({ path: newPath }));
    } else {
        store.dispatch(historyLocationActions.goTo({ path: newPath }));
    }
}

function formatCanadianPostalCode(postalCode) {
    const normalized = (postalCode || '').replace(/[^0-9A-Z]/gi, '').toUpperCase();

    return normalized.length === 6 ? `${normalized.slice(0, 3)} ${normalized.slice(3)}` : normalized;
}

function setRequestDataForCreditCardUpdate(pendingCreditCard, securityCode) {
    const {
        cardType, lastName, firstName, creditCardId, expirationMonth, expirationYear
    } = pendingCreditCard;
    const requestData = {
        isMarkAsDefault: pendingCreditCard.isDefault,
        creditCard: {
            address: {
                ...pendingCreditCard.address
            },
            creditCardId,
            firstName,
            lastName,
            cardType,
            expirationMonth,
            expirationYear
        }
    };

    if (securityCode) {
        requestData.creditCard.securityCode = securityCode;
    }

    return requestData;
}

function getActiveAlternatePaymentFromOrder(orderDetails) {
    const paymentGroupsEntries = orderDetails?.paymentGroups?.paymentGroupsEntries || [];

    const alternatePaymentGroupTypes = {
        [OrderUtils.PAYMENT_GROUP_TYPE.PAYPAL]: ALTERNATE_PAYMENT_TYPES.PAYPAL,
        [OrderUtils.PAYMENT_GROUP_TYPE.VENMO]: ALTERNATE_PAYMENT_TYPES.VENMO,
        [OrderUtils.PAYMENT_GROUP_TYPE.KLARNA]: ALTERNATE_PAYMENT_TYPES.KLARNA,
        [OrderUtils.PAYMENT_GROUP_TYPE.AFTERPAY]: ALTERNATE_PAYMENT_TYPES.AFTERPAY,
        [OrderUtils.PAYMENT_GROUP_TYPE.PAZE]: ALTERNATE_PAYMENT_TYPES.PAZE
    };

    for (const entry of paymentGroupsEntries) {
        const paymentType = alternatePaymentGroupTypes[entry.paymentGroupType];

        // PayPal is handled differently: the payment group is only marked as complete after the user approves PayPal,
        // but we want to show it as completed in the review page before placing the order. Check if it's PayPal
        // and treat it as completed even if not yet marked as complete.
        const isPayPalEntry = entry.paymentGroupType === OrderUtils.PAYMENT_GROUP_TYPE.PAYPAL;

        if (paymentType && entry.paymentGroup && (entry.paymentGroup.isComplete || isPayPalEntry)) {
            return paymentType;
        }
    }

    return null;
}

/**
 * Helper: Detects if the first argument is an options object
 */
function isOptionsObject(optionsOrOrderDetails) {
    return (
        optionsOrOrderDetails &&
        typeof optionsOrOrderDetails === 'object' &&
        !Array.isArray(optionsOrOrderDetails) &&
        ('orderDetails' in optionsOrOrderDetails ||
            'addressList' in optionsOrOrderDetails ||
            'hasValidatedPayPalAddress' in optionsOrOrderDetails ||
            'onUnverified' in optionsOrOrderDetails ||
            'onRecommended' in optionsOrOrderDetails ||
            'onCancel' in optionsOrOrderDetails ||
            'setHasValidated' in optionsOrOrderDetails ||
            'validateAddressFn' in optionsOrOrderDetails ||
            'pageType' in optionsOrOrderDetails ||
            'isReleaseThreeVerifyAddressModal' in optionsOrOrderDetails)
    );
}

/**
 * Helper: Normalizes arguments to options object
 */
function normalizeValidationOptions(optionsOrOrderDetails, rest) {
    if (isOptionsObject(optionsOrOrderDetails)) {
        return optionsOrOrderDetails;
    }

    // Fallback: support legacy positional arguments
    const [addressList, hasValidatedPayPalAddress, onUnverified, setHasValidated, validateAddressFn, pageType, isReleaseThreeVerifyAddressModal] =
        rest;

    return {
        orderDetails: optionsOrOrderDetails,
        addressList,
        hasValidatedPayPalAddress,
        onUnverified,
        setHasValidated,
        validateAddressFn,
        pageType,
        isReleaseThreeVerifyAddressModal
    };
}

/**
 * Helper: Checks if validation should proceed
 */
function shouldValidatePayPalAddress({ orderDetails, hasValidatedPayPalAddress, paypalPaymentGroup, paypalAddress }) {
    if (!orderDetails || hasValidatedPayPalAddress || !paypalPaymentGroup || !paypalAddress) {
        return false;
    }

    // We're in validatePayPalAddress with a confirmed PayPal payment/address
    // (getPayPalPaymentGroup and getPayPalShippingAddress already filtered for PayPal)
    // Per requirement: "Address validation must bypass backend auto-validated PayPal addresses"
    // Always validate PayPal addresses, even if saved, because PayPal can return
    // invalid data (4-character state codes, etc.)
    return true;
}

/**
 * Validates PayPal-provided shipping address via AVS
 *
 * Preferred usage:
 * validatePayPalAddress({
 *     orderDetails,
 *     addressList,
 *     hasValidatedPayPalAddress,
 *     onUnverified,
 *     onRecommended,
 *     onCancel,
 *     setHasValidated,
 *     validateAddressFn,
 *     pageType,
 *     isReleaseThreeVerifyAddressModal
 * });
 *
 * Legacy usage with positional arguments is still supported for backward compatibility.
 *
 * @param {Object|*} optionsOrOrderDetails - Options object (preferred) or order details (legacy positional)
 * @param {...*} rest - Legacy positional arguments: addressList, hasValidatedPayPalAddress, onUnverified, setHasValidated, validateAddressFn, pageType, isReleaseThreeVerifyAddressModal
 * @returns {boolean} - Returns true if validation was executed, false if skipped
 */
function validatePayPalAddress(optionsOrOrderDetails, ...rest) {
    const options = normalizeValidationOptions(optionsOrOrderDetails, rest);

    const {
        orderDetails,
        hasValidatedPayPalAddress,
        onUnverified,
        onRecommended,
        onCancel,
        setHasValidated,
        validateAddressFn,
        pageType,
        isReleaseThreeVerifyAddressModal = false
    } = options;

    const paypalPaymentGroup = OrderUtils.getPayPalPaymentGroup(orderDetails);
    let paypalAddress = OrderUtils.getPayPalShippingAddress(orderDetails);

    // Fallback: If address not found in shipping groups, try getting it from payment group
    if (!paypalAddress && paypalPaymentGroup?.address) {
        paypalAddress = paypalPaymentGroup.address;
    }

    // Check if validation should proceed
    if (
        !shouldValidatePayPalAddress({
            orderDetails,
            hasValidatedPayPalAddress,
            paypalPaymentGroup,
            paypalAddress
        })
    ) {
        return false; // Return false to indicate validation was skipped
    }

    // Validate that we have the validateAddressFn before proceeding
    if (typeof validateAddressFn !== 'function') {
        return false; // Return false when no validation function is provided
    }

    const addressData = {
        // according to API, address1 param should include address1 and address2
        address1: `${paypalAddress.address1} ${paypalAddress.address2 ?? ''}`.trim(),
        state: paypalAddress.state,
        city: paypalAddress.city,
        postalCode: paypalAddress.postalCode,
        country: paypalAddress.country
    };

    const successCallbacks = {
        UNVERIFIED: () => {
            if (typeof onUnverified === 'function') {
                onUnverified();
            }
        },
        RECOMMENDED: recommendedAddress => {
            if (typeof onRecommended === 'function') {
                onRecommended(recommendedAddress);
            }
            // If no custom handler, treat RECOMMENDED as validated (do nothing)
        }
    };

    const cancelCallback = () => {
        if (typeof onCancel === 'function') {
            onCancel();
        }
        // If no custom handler, do nothing (default behavior)
    };

    // Call the validation function
    validateAddressFn(
        addressData,
        successCallbacks,
        cancelCallback,
        pageType,
        false, // shouldRefreshCheckout
        isReleaseThreeVerifyAddressModal
    );

    // Mark as validated AFTER successfully calling the API
    // This prevents duplicate calls and ensures we only mark as validated when validation actually ran
    if (typeof setHasValidated === 'function') {
        setHasValidated();
    }

    return true; // Return true to indicate validation was executed
}

function hasSavedOrDefaultAlternatePayment(paymentOptions, orderDetails) {
    const defaultPayment = paymentOptions?.defaultPayment;

    const isDefault = ALTERNATE_PAYMENT_LIST.includes(defaultPayment);
    const activeInOrder = getActiveAlternatePaymentFromOrder(orderDetails);

    return isDefault || !!activeInOrder;
}

function getSavedAlternatePaymentUsername(paymentOptions, orderDetails) {
    const activePayment = getActiveAlternatePaymentFromOrder(orderDetails);
    const paymentToUse = activePayment || paymentOptions?.defaultPayment;

    switch (paymentToUse) {
        case ALTERNATE_PAYMENT_TYPES.PAYPAL:
            return paymentOptions?.paypal?.email || '';
        case ALTERNATE_PAYMENT_TYPES.VENMO:
            return OrderUtils.getPaymentGroup(orderDetails, VENMO_PAYMENT_GROUP)?.username || '';
        case ALTERNATE_PAYMENT_TYPES.KLARNA:
        case ALTERNATE_PAYMENT_TYPES.AFTERPAY:
        case ALTERNATE_PAYMENT_TYPES.PAZE:
            // These don't typically show username/email in saved state
            return '';
        default:
            return '';
    }
}

function getUsernameForPaymentType(paymentType, paymentOptions, orderDetails) {
    switch (paymentType) {
        case ALTERNATE_PAYMENT_TYPES.PAYPAL:
            return paymentOptions?.paypal?.email || '';
        case ALTERNATE_PAYMENT_TYPES.VENMO:
            return OrderUtils.getPaymentGroup(orderDetails, VENMO_PAYMENT_GROUP)?.username || '';
        default:
            return '';
    }
}

function getSavedAlternatePayments(paymentOptions, orderDetails, hasPendingCreditCard) {
    const paymentTypeMap = {
        [ALTERNATE_PAYMENT_TYPES.PAYPAL]: ALTERNATE_PAYMENT_TYPES.PAYPAL,
        [ALTERNATE_PAYMENT_TYPES.VENMO]: PAYMENT_TYPES.PAY_VENMO,
        [ALTERNATE_PAYMENT_TYPES.KLARNA]: PAYMENT_TYPES.PAY_KLARNA,
        [ALTERNATE_PAYMENT_TYPES.AFTERPAY]: PAYMENT_TYPES.PAY_AFTERPAY,
        [ALTERNATE_PAYMENT_TYPES.PAZE]: PAYMENT_TYPES.PAY_PAZE
    };

    const payments = [];
    const activePayment = getActiveAlternatePaymentFromOrder(orderDetails);
    const defaultPayment = paymentOptions?.defaultPayment;

    if (activePayment && !(hasPendingCreditCard && activePayment !== defaultPayment)) {
        payments.push({
            type: activePayment,
            paymentName: paymentTypeMap[activePayment],
            username: getUsernameForPaymentType(activePayment, paymentOptions, orderDetails)
        });
    }

    if (ALTERNATE_PAYMENT_LIST.includes(defaultPayment) && defaultPayment !== activePayment) {
        payments.push({
            type: defaultPayment,
            paymentName: paymentTypeMap[defaultPayment],
            username: getUsernameForPaymentType(defaultPayment, paymentOptions, orderDetails)
        });
    }

    return payments;
}

/**
 * Returns the unified active payment item — the alt payment or credit card currently in the order.
 * Alt payments take precedence if both exist (one shouldn't, but pick deterministically).
 * Shape: { kind: 'alt' | 'cc', data } | null
 *  - 'alt' data is { type, paymentName, username } (matches getSavedAlternatePayments entry shape)
 *  - 'cc' data is the credit card object from paymentOptions.creditCards
 */
function getActivePaymentItem(paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard) {
    const activeAltType = getActiveAlternatePaymentFromOrder(orderDetails);

    if (activeAltType) {
        // Reuse getSavedAlternatePayments so the alt item shape matches what SavedAlternatePaymentRadio expects.
        // The helper may omit the active alt when there's a pending CC mismatch — fall through to CC in that case.
        const activeAlt = getSavedAlternatePayments(paymentOptions, orderDetails, hasPendingCreditCard).find(p => p.type === activeAltType);

        if (activeAlt) {
            return { kind: 'alt', data: activeAlt };
        }
    }

    const activeCcId = creditCardPaymentGroup?.creditCardId;
    const activeCc = activeCcId ? (paymentOptions?.creditCards || []).find(c => c.creditCardId === activeCcId) : null;

    return activeCc ? { kind: 'cc', data: activeCc } : null;
}

/**
 * Returns the user's default payment item, but only if it differs from the active item.
 * If default is an alt payment in ALTERNATE_PAYMENT_LIST, returns that. Otherwise returns the
 * card with isDefault === true (when defaultPayment is 'creditCard' or unspecified).
 */
function getDefaultPaymentItem(activeItem, paymentOptions, orderDetails, hasPendingCreditCard) {
    const defaultPayment = paymentOptions?.defaultPayment;
    const defaultAlt = getSavedAlternatePayments(paymentOptions, orderDetails, hasPendingCreditCard).find(p => p.type === defaultPayment);

    if (defaultAlt) {
        const isSameAsActive = activeItem?.kind === 'alt' && activeItem.data.type === defaultAlt.type;

        return isSameAsActive ? null : { kind: 'alt', data: defaultAlt };
    }

    const defaultCc = (paymentOptions?.creditCards || []).find(c => c.isDefault);

    if (defaultCc) {
        const isSameAsActive = activeItem?.kind === 'cc' && activeItem.data.creditCardId === defaultCc.creditCardId;

        return isSameAsActive ? null : { kind: 'cc', data: defaultCc };
    }

    return null;
}

/**
 * Returns the priority payment items (active first, default second) in display order.
 * Either may be missing.
 */
function getPriorityPaymentItems(paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard) {
    const activeItem = getActivePaymentItem(paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard);
    const defaultItem = getDefaultPaymentItem(activeItem, paymentOptions, orderDetails, hasPendingCreditCard);

    return [activeItem, defaultItem].filter(Boolean);
}

/**
 * Picks the credit card to auto-select when entering the payment edit view: prefer the card
 * already in the order, otherwise fall back to the user's default card. Cards that failed
 * token migration are excluded from both passes.
 */
function findCardToAutoSelect(cards) {
    const safeCards = cards || [];

    return (
        safeCards.find(c => !!c.isCardInOrder && creditCardUtils.tokenMigrationDisabledOrSucceed(c)) ||
        safeCards.find(c => !!c.isDefault && creditCardUtils.tokenMigrationDisabledOrSucceed(c))
    );
}

/**
 * Picks a credit card to promote to default before deleting the current default. Skips the
 * card being deleted and any card that failed token migration (those can't be promoted).
 */
function findNextDefaultCardCandidate(cards, currentCardId) {
    return (cards || []).find(c => !creditCardUtils.tokenMigrationFailed(c) && c.creditCardId !== currentCardId);
}

function showCVVModal(creditCard, orderDetails) {
    if (creditCardUtils.tokenMigrationFailed(creditCard)) {
        return false;
    }

    // Agent-aware logic
    if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
        const forceCVC = !OrderUtils.isZeroCheckout() && !agentAwareUtils.isAgentAuthorized(['3']);

        if (forceCVC) {
            Storage.session.setItem(LOCAL_STORAGE.EDIT_SEPHORA_CARD, false);
        }

        return forceCVC;
    }

    const ccComplete = creditCard.isCardInOrder && CheckoutUtils.isPaymentInOrderComplete() && OrderUtils.getCreditCardPaymentGroup(orderDetails);
    const isEfulfilledRewardWithCVVValidation = !OrderUtils.isShippableOrder(orderDetails) && OrderUtils.isZeroDollarOrderWithCVVValidation();
    const showCVCInput =
        !ccComplete && (!creditCard.isPreApproved || isEfulfilledRewardWithCVVValidation) && !OrderUtils.isSephoraTempCardType(creditCard);

    return showCVCInput;
}

/**
 * Merges total available store credits from user profile with applied store credits from order
 * Filters credits by current locale currency and provides both individual full amount and applied amount for each AC
 * @param {Array} userStoreCredits - Total available store credits from user profile (may contain BOTH US and CAD credits)
 * @param {Array} appliedStoreCredits - Applied store credits from order payment groups (only for current locale)
 * @returns {Array} - Array of store credits with both amount (individual full amount) and appliedAmount (applied to order)
 */
function mergeStoreCreditsData(userStoreCredits = [], appliedStoreCredits = []) {
    if (!appliedStoreCredits || appliedStoreCredits.length === 0) {
        return [];
    }

    // Determine current currency from current locale (only current locale AC are applied to order)
    const currentCurrency = localeUtils.isCanada() ? 'CAD' : 'USD';

    // CRITICAL: Filter user credits to match ONLY the current locale's currency
    // This ensures we only show CAD credits on Canadian site, USD credits on US site
    const userStoreCreditsForCurrentLocale = userStoreCredits.filter(credit => {
        // If no currency metadata, keep the credit
        if (!credit?.currency) {
            return true;
        }

        // STRICT: Only include credits that match the current currency
        return credit.currency === currentCurrency;
    });

    // Map applied credits and match with user credits by index to get individual full amounts
    return appliedStoreCredits.map((appliedCredit, index) => {
        // Match with user credit by index to get full available amount for this specific AC
        const matchingUserCredit = userStoreCreditsForCurrentLocale[index];

        // Use the original amount string from matching user credit (preserves currency format like "$10 CAD")
        const fullAmountStr = matchingUserCredit?.amount;
        const appliedAmountStr = appliedCredit.amount;

        return {
            ...appliedCredit,
            amount: fullAmountStr, // Individual full available amount for this specific AC (preserves original format)
            appliedAmount: appliedAmountStr, // Amount applied to current order
            expirationDate: appliedCredit.expirationDate || matchingUserCredit?.expirationDate || null
        };
    });
}

export default {
    checkPromoEligibility,
    setShippingMethod,
    getPaymentSectionProps,
    paymentServices,
    checkForCheckoutSectionsCompleteness,
    getErrorStatesFromSubmitOrderAPI,
    SECTION_NAMES,
    getOrderFraudError,
    setErrorsFromCheckoutAPI,
    getPaymentMethodName,
    getPromosData,
    validatePaymentGroup,
    setDeliverToErrors,
    isFCFinalReleaseEnabled,
    sectionThatNeedsToBeConfirmed,
    isPaymentComplete,
    isDeliverToComplete,
    isGiftCardDeliveryComplete,
    isPickupInfoComplete,
    SECTION_COMPLETION_CHECKERS,
    updateCheckoutURLBasedOnOrderCompleteness,
    formatCanadianPostalCode,
    isCVVMissing,
    isCVVMissingForBopis,
    setRequestDataForCreditCardUpdate,
    getActiveAlternatePaymentFromOrder,
    hasSavedOrDefaultAlternatePayment,
    getSavedAlternatePaymentUsername,
    getSavedAlternatePayments,
    getActivePaymentItem,
    getDefaultPaymentItem,
    getPriorityPaymentItems,
    isHigherRedeemPointsOrder,
    findCardToAutoSelect,
    findNextDefaultCardCandidate,
    validatePayPalAddress,
    mergeStoreCreditsData,
    showCVVModal
};
